/**
 * PAAB App Filter Mappings - 004-enhanced-metadata-filters
 *
 * Defines mappings from abstracted UI filter values to discrete Teamwork values.
 * These mappings are used client-side in PAAB App to construct OpenAI filters.
 *
 * Location: paab/src/config/filter-mappings.ts
 */

// ========== APS Category Mapping ==========

/**
 * Maps abstracted APS Category values to discrete Teamwork values
 *
 * UI shows: "New", "Update"
 * OpenAI stores: "New piece", "Update to previously approved piece", etc.
 *
 * Note: "Internal" projects are EXCLUDED from all category filters
 */
export const APS_CATEGORY_MAPPING: Record<string, string[]> = {
  'New': [
    'New piece'
  ],
  'Update': [
    'Update to previously approved piece',
    'Format conversion',
    'Variant',
    'Renewal (no changes)'
  ]
  // 'Internal' is explicitly excluded - never shown in UI
};

/**
 * Get all category values (for fetching available options)
 */
export const APS_CATEGORY_OPTIONS = Object.keys(APS_CATEGORY_MAPPING);

// ========== APS Sub-category Mapping ==========

/**
 * Maps abstracted APS Sub-category values to discrete Teamwork values
 *
 * UI shows: 13 abstracted values (e.g., "Email", "Journal or banner ad")
 * OpenAI stores: 28 discrete values (e.g., "Email - Veeva deployed", "Banner ad (static)")
 */
export const APS_SUBCATEGORY_MAPPING: Record<string, string[]> = {
  // Multi-value mappings
  'Email': [
    'Email - Veeva deployed',
    'Email - Head office deployed',
    'Email - Outlook template',
    'Email - PTM deployed'
  ],
  'Journal or banner ad': [
    'Banner ad (static)',
    'Banner ad (dynamic)',
    'Journal ad (complex)',
    'Journal ad (simple)'
  ],
  'Conference materials': [
    'Conference booth',
    'Pull-up banner',
    'Table-top display'
  ],
  'Detail aid': [
    'Detail aid (digital - Veeva platform)',
    'Detail aid (print)'
  ],
  'Forms / Fax': [
    'Enrolment Form',
    'Fax'
  ],
  'Leave behinds': [
    'Interactive PDF',
    'Leave-behind / leaflet',
    'Post card',
    'Reprint carrier'
  ],
  'Social media': [
    'Social media (ad)',
    'Social media (post)'
  ],
  'Video': [
    'Video - animated',
    'Video - live action'
  ],

  // 1:1 mappings (abstracted value === discrete value)
  'Digital app': ['Digital app'],
  'Service oriented vehicle': ['Service oriented vehicle (SOV)'],
  'Slide deck': ['Slide deck'],
  'Starter kit': ['Starter kit related'],
  'Website': ['Website']
};

/**
 * Get all sub-category values (for fetching available options)
 */
export const APS_SUBCATEGORY_OPTIONS = Object.keys(APS_SUBCATEGORY_MAPPING);

// ========== Helper Functions ==========

/**
 * Get discrete values from abstracted category value
 * @returns Array of discrete values, or empty array if not found
 */
export function getCategoryDiscreteValues(abstractedValue: string): string[] {
  return APS_CATEGORY_MAPPING[abstractedValue] || [];
}

/**
 * Get discrete values from abstracted sub-category value
 * @returns Array of discrete values, or empty array if not found
 */
export function getSubcategoryDiscreteValues(abstractedValue: string): string[] {
  return APS_SUBCATEGORY_MAPPING[abstractedValue] || [];
}

/**
 * Get all discrete values for multiple abstracted values
 * @param abstractedValues Array of abstracted values selected by user
 * @param mapping The mapping to use (category or sub-category)
 * @returns Flat array of all discrete values
 */
export function getAllDiscreteValues(
  abstractedValues: string[],
  mapping: Record<string, string[]>
): string[] {
  return abstractedValues.flatMap(value => mapping[value] || []);
}

/**
 * Validate that an abstracted value exists in the mapping
 */
export function isValidCategoryValue(value: string): boolean {
  return value in APS_CATEGORY_MAPPING;
}

/**
 * Validate that an abstracted value exists in the mapping
 */
export function isValidSubcategoryValue(value: string): boolean {
  return value in APS_SUBCATEGORY_MAPPING;
}

// ========== Reverse Mapping (for display) ==========

/**
 * Get abstracted category from discrete value (for result display)
 * @returns Abstracted value or null if not found
 */
export function getAbstractedCategory(discreteValue: string): string | null {
  for (const [abstracted, discrete] of Object.entries(APS_CATEGORY_MAPPING)) {
    if (discrete.includes(discreteValue)) {
      return abstracted;
    }
  }
  return discreteValue === 'Internal' ? 'Internal' : null;
}

/**
 * Get abstracted sub-category from discrete value (for result display)
 * @returns Abstracted value or null if not found
 */
export function getAbstractedSubcategory(discreteValue: string): string | null {
  for (const [abstracted, discrete] of Object.entries(APS_SUBCATEGORY_MAPPING)) {
    if (discrete.includes(discreteValue)) {
      return abstracted;
    }
  }
  return null;
}

// ========== Type Definitions ==========

export type APSCategoryValue = keyof typeof APS_CATEGORY_MAPPING;
export type APSSubcategoryValue = keyof typeof APS_SUBCATEGORY_MAPPING;

// ========== Example Usage ==========

/**
 * Example: User selects "Email" and "Video" in UI
 */
/*
const selectedSubcategories: APSSubcategoryValue[] = ['Email', 'Video'];
const discreteValues = getAllDiscreteValues(selectedSubcategories, APS_SUBCATEGORY_MAPPING);
// Result: ['Email - Veeva deployed', 'Email - Head office deployed', 'Email - Outlook template',
//          'Email - PTM deployed', 'Video - animated', 'Video - live action']

// Build OpenAI filter
const filter = {
  type: 'in',
  key: 'aps_subcategory',
  value: discreteValues
};
*/

/**
 * Example: Display result with discrete value
 */
/*
const result = { aps_subcategory: 'Email - Veeva deployed' };
const displayValue = getAbstractedSubcategory(result.aps_subcategory);
// displayValue = 'Email'
*/
